import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../ApiProvider';
import { ApiError, unwrap } from '../client';
import type { components } from '../schema.gen';
import { queryKeys } from './keys';

export type OrgImportPreview = components['schemas']['OrgImportPreview'];
export type ErrorReport = components['schemas']['ErrorReport'];

/** A rejected file's report (the preview's 422), or undefined for any other error. */
export function errorReportOf(error: unknown): ErrorReport | undefined {
  if (!(error instanceof ApiError)) return undefined;
  return (error.problem.errorReport as ErrorReport | null | undefined) ?? undefined;
}

/** The current org structure as .xlsx, with the API's file name. */
export function useExportOrgStructure() {
  const api = useApiClient();
  return useMutation({
    mutationFn: async (): Promise<{ blob: Blob; fileName: string }> => {
      const result = await api.GET('/api/admin/org-structure/export', { parseAs: 'blob' });
      const blob = await unwrap(Promise.resolve(result));
      const disposition = result.response.headers.get('Content-Disposition') ?? '';
      const name = /filename="([^"]+)"/.exec(disposition);
      return { blob, fileName: name?.[1] ?? 'org-structure.xlsx' };
    },
  });
}

/** Reads a file and returns what importing it would change; nothing changes yet. */
export function usePreviewOrgImport() {
  const api = useApiClient();
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file);
      return unwrap(
        api.POST('/api/admin/org-structure/imports', {
          // A FormData body is sent as is, with its multipart boundary.
          body: body as unknown as { file: string },
        }),
      );
    },
  });
}

/** Applies a preview, then refreshes every user list. */
export function useApplyOrgImport() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (importId: string) =>
      unwrap(
        api.POST('/api/admin/org-structure/imports/{import_id}/apply', {
          params: { path: { import_id: importId } },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers() }),
  });
}
